import { useState } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { ArrowIcon, ArrowIconVariant } from '@repo/ui/icons';

import type { RisingStarBonusCurrent } from 'services/hooks/risingStarBonus';

import Flex from 'components/util/Flex';

import { useRisingStarCopy } from './copy';
import { formatAmount } from './format';
import { STitle } from './style';
import { formatKstDateTime } from '../minimumGuarantee/utils/formatSettlement';
import AmountValue from '../shared/AmountValue';
import { SInformationItemWrapper } from '../shared/style';

/** 접힌 상태에서 보여줄 개수 (시안) */
const COLLAPSED_COUNT = 3;

type Props = { assets: RisingStarBonusCurrent['assets']; isBlur: boolean };

/**
 * 산정 대상 애셋 목록 (정책 B-1). 어떤 애셋이 오는지는 서버가 정하고 그대로 그린다.
 * 합계·대상 수는 더하거나 세지 않고 응답 값을 쓴다 — 제외 애셋(isExcluded)은 목록에 있어도 합계·대상 수에 없다.
 * 제외 애셋은 이름 취소선(`<del>`) + '제외' 라벨로 남긴다 (디자인 확인 5번 답변, 10/6).
 */
export default function BonusAssetList({ assets: { items, targetCount, totalReward }, isBlur }: Props) {
  const COPY = useRisingStarCopy();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  if (items.length === 0) {
    return (
      <SEmpty>
        <strong>{COPY.emptyTitle}</strong>
        <span>{COPY.emptyBody}</span>
      </SEmpty>
    );
  }

  const visible = isOpen ? items : items.slice(0, COLLAPSED_COUNT);

  return (
    <Flex direction="column">
      <STitle>
        {COPY.assetTitle} {targetCount} · {COPY.assetSub}
      </STitle>
      {visible.map((a) => (
        <SInformationItemWrapper key={`${a.sourceType}-${a.sourceId}`}>
          <SAsset>
            {/* del 은 브라우저 기본 취소선이고, 보조기기에도 '삭제됨'으로 읽힌다 */}
            <SName as={a.isExcluded ? 'del' : undefined}>{a.name}</SName>
            <SMeta>
              {COPY.assetType[a.sourceType]} · {COPY.firstPublished}{' '}
              {formatKstDateTime(a.firstPublishedAt)?.slice(0, 10) ?? '-'}
              {a.isExcluded && <SExcluded>{COPY.excluded}</SExcluded>}
            </SMeta>
          </SAsset>
          <AmountValue amountFormat={formatAmount(a.reward)} isBlur={isBlur} />
        </SInformationItemWrapper>
      ))}
      {items.length > COLLAPSED_COUNT && (
        <SToggle type="button" aria-expanded={isOpen} onClick={() => setIsOpen((v) => !v)}>
          {/* 토글 노출 조건과 같은 기준(items) — 펼치면 정확히 이 개수가 보인다 */}
          <span>{isOpen ? COPY.collapse : COPY.expand(items.length)}</span>
          {/* 세로 화살표(아래)를 펼친 상태에서 180° 돌린다 — TagSelector 와 같은 방식 */}
          <ArrowIcon
            variant={ArrowIconVariant.VERTICAL}
            size={16}
            rotate={isOpen ? 180 : 0}
            color={theme.color.label.secondary}
          />
        </SToggle>
      )}
      <SInformationItemWrapper $invisibleBorderBottom>
        <span>{COPY.total}</span>
        <AmountValue amountFormat={formatAmount(totalReward)} isBlur={isBlur} />
      </SInformationItemWrapper>
    </Flex>
  );
}

const SAsset = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SName = styled.strong`
  color: ${({ theme }) => theme.color.label.primary};
  ${({ theme }) => theme.font.body2_m};
`;

const SMeta = styled.small`
  color: ${({ theme }) => theme.color.label.tertiary};
  ${({ theme }) => theme.font.caption1_m};
`;

const SExcluded = styled.em`
  margin-left: 6px;
  color: ${({ theme }) => theme.color.label.tertiary};
  ${({ theme }) => theme.font.caption1_sb};
  font-style: normal;
`;

// v2 댓글 섹션 토글(V2CommentSection .toggle)과 같은 결 — 테두리·배경 없이 글자 + 셰브론, 목록 아래 가운데
const SToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 14px 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.subtle};
  background: none;
  color: ${({ theme }) => theme.color.label.secondary};
  ${({ theme }) => theme.font.body2_sb};
  cursor: pointer;

  .dds-icon {
    transition: transform 0.2s ease;
  }
`;

const SEmpty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px 0;
  color: ${({ theme }) => theme.color.label.secondary};
  ${({ theme }) => theme.font.body2_m};
  text-align: center;
`;
